import { NotificationTag, useAuth, useOneSignalCustomState } from '../state';
import { useEffect, useState } from 'react';

import OneSignal from 'react-onesignal';
import { PushNotification } from '../services/notifications';
import getConfig from 'next/config';
import { nanoid } from 'nanoid';
import shallow from 'zustand/shallow';
import { useAddToken } from '@/services';
import { useNotifications } from './use-notifications';

const OneSignalAppId = getConfig().publicRuntimeConfig.oneSignalAppId as string;
export const USER_TYPE = 'user_type';

// enum OneSignalEvents {
//   subscriptionChange = 'subscriptionChange',
//   permissionPromptDisplay = 'permissionPromptDisplay',
//   notificationPermissionChange = 'notificationPermissionChange',
//   popoverShown = 'popoverShown',
//   customPromptClick = 'customPromptClick',
//   notificationDisplay = 'notificationDisplay',
//   notificationDismiss = 'notificationDismiss',
// }

export const useOneSignal = () => {
  const [oneSignal, setOneSignal] = useState<typeof OneSignal>(null);
  const [oneSignalUserId, setOneSignalUserId] = useState<string>('');
  const user = useAuth(s => s.user, shallow);
  const [deviceId, setDeviceId, oneSignalUserType, setOneSignalUserType] =
    useOneSignalCustomState(
      s => [s.muviDeviceId, s.setDeviceId, s.oneSignalUserType, s.setOneSignalUserType],
      shallow
    );
  const _useAddToken = useAddToken();
  const { refetch, handleNotificationClick } = useNotifications();

  const id = nanoid();

  useEffect(() => {
    if (!!oneSignal) return;
    OneSignal.init({
      appId: OneSignalAppId,
      allowLocalhostAsSecureOrigin: true,
    }).then(() => {
      setOneSignal(OneSignal);
      OneSignal.Debug.setLogLevel('trace');
      setOneSignalUserId(OneSignal.User.PushSubscription.id);
      OneSignal.Notifications.addEventListener('click', ({ notification }) => {
        const _notification = notification as unknown as Partial<PushNotification>;
        const { additionalData } = _notification;
        handleNotificationClick({
          id: additionalData.notificationId,
          type: additionalData.type,
          objectId: additionalData.target,
          objectUniqueName: additionalData.targetUniqueName,
          externalLink: additionalData.target,
          isRead: false,
          isPush: true,
          templateNotificationId: additionalData.templateNotificationId,
        });
      });
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oneSignal, user]);

  useEffect(() => {
    if (!oneSignal) return;
    setOneSignalUserId(oneSignal.User.PushSubscription.id);
    oneSignal.User.PushSubscription.optIn();
    oneSignal.Notifications.addEventListener('foregroundWillDisplay', () => {
      if (user?.token) {
        refetch();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oneSignal]);

  const handleAddToken = () => {
    oneSignal.login(user.id.toString());
    _useAddToken.safeMutateAsync(
      {
        headers: {
          'X-Device-Id': deviceId,
        },
        data: {
          token: oneSignalUserId,
        },
      },
      {
        onSuccess: () => {
          setOneSignalUserType(NotificationTag.AUTH);
        },
      }
    );
  };

  useEffect(() => {
    if (!user || !oneSignalUserId || !deviceId) {
      oneSignal && joinGuestSegment();
    }
    if (!!user && !!oneSignalUserId && !!deviceId) {
      leaveGuestSegment(handleAddToken);
    }
    if (!!oneSignalUserId) {
      handleDeviceId();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, oneSignalUserId, deviceId, oneSignal]);

  /**
   * If the deviceId is not in localStorage, set it to the oneSignalUserId and the id
   */
  const handleDeviceId = (): void => {
    setDeviceId(deviceId || `${oneSignalUserId}-${id}`);
  };

  /**
   * If the user is already a guest, do nothing. Otherwise, set the user type to guest
   * @returns the value of the variable oneSignalUserType.
   */
  const joinGuestSegment = (): void => {
    // if (oneSignalUserType === NotificationTag.GUEST) return;
    // else {
    oneSignal.User.addTag(USER_TYPE, NotificationTag.GUEST);
    // oneSignal.logout();
    setOneSignalUserType(NotificationTag.GUEST);
    // }
  };

  /**
   * If the user is not an authenticated user, delete the user type tag and then call the callback
   * function
   * @param callback - () => void
   * @returns the value of the callback function.
   */
  const leaveGuestSegment = (callback: () => void): void => {
    // if (oneSignalUserType === NotificationTag.AUTH) {
    // } else {
    oneSignal.User.addTag(USER_TYPE, NotificationTag.LOGGED_IN);
    setOneSignalUserType(NotificationTag.AUTH);
    callback();
    // }
  };

  return {};
};
